"use client";

import { CheckIcon, CopyIcon, ExternalLinkIcon } from "lucide-react";
import { memo, useCallback, useState } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { getChainInfo } from "./types";

/**
 * Truncate an address for display
 */
export function truncateAddress(
  address: string,
  startChars = 6,
  endChars = 4
): string {
  if (address.length <= startChars + endChars + 3) {
    return address;
  }
  return `${address.slice(0, startChars)}...${address.slice(-endChars)}`;
}

export type AddressDisplayProps = {
  /** The full address to display */
  address: string;
  /** Chain ID for explorer link */
  chainId?: string;
  /** Whether to truncate the address */
  truncate?: boolean;
  /** Number of characters to show at start when truncating */
  startChars?: number;
  /** Number of characters to show at end when truncating */
  endChars?: number;
  /** Whether to show copy button */
  showCopy?: boolean;
  /** Whether to show external link to explorer */
  showExplorerLink?: boolean;
  /** Optional label to show before the address */
  label?: string;
  /** Optional known name for the address (e.g., "Uniswap V3") */
  knownName?: string;
  /** Whether this is a contract address */
  isContract?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Size variant */
  size?: "sm" | "md" | "lg";
  /** Optional click handler for the address itself */
  onClick?: () => void;
};

const PureAddressDisplay = ({
  address,
  chainId,
  truncate = true,
  startChars = 6,
  endChars = 4,
  showCopy = true,
  showExplorerLink = true,
  label,
  knownName,
  isContract,
  className,
  size = "md",
  onClick,
}: AddressDisplayProps) => {
  const [copied, setCopied] = useState(false);

  const displayAddress = truncate
    ? truncateAddress(address, startChars, endChars)
    : address;

  const chain = chainId ? getChainInfo(chainId) : null;
  const explorerUrl = chain?.explorerUrl
    ? `${chain.explorerUrl}/address/${address}`
    : null;

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Silently fail if clipboard access is denied
    }
  }, [address]);

  const sizeClasses = {
    sm: "text-xs",
    md: "text-sm",
    lg: "text-base",
  };

  const iconSizes = {
    sm: "size-3",
    md: "size-3.5",
    lg: "size-4",
  };

  return (
    <TooltipProvider>
      <span
        className={cn(
          "inline-flex items-center gap-1.5",
          sizeClasses[size],
          className
        )}
      >
        {label && <span className="text-muted-foreground">{label}</span>}

        <Tooltip>
          <TooltipTrigger asChild>
            <button
              className={cn(
                "inline-flex items-center gap-1 font-mono transition-colors",
                onClick
                  ? "cursor-pointer hover:text-primary"
                  : "cursor-default",
                isContract && "text-amber-600 dark:text-amber-400"
              )}
              onClick={onClick}
              type="button"
            >
              {knownName ? (
                <span className="font-medium font-sans">{knownName}</span>
              ) : (
                <span>{displayAddress}</span>
              )}
              {isContract && (
                <span className="text-[10px] text-muted-foreground uppercase tracking-wider">
                  ⚡
                </span>
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent className="font-mono text-xs">
            <p>{address}</p>
            {knownName && (
              <p className="mt-1 font-sans text-muted-foreground">
                {knownName}
              </p>
            )}
          </TooltipContent>
        </Tooltip>

        {showCopy && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                className="text-muted-foreground transition-colors hover:text-foreground"
                onClick={handleCopy}
                type="button"
              >
                {copied ? (
                  <CheckIcon
                    className={cn(iconSizes[size], "text-green-500")}
                  />
                ) : (
                  <CopyIcon className={iconSizes[size]} />
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{copied ? "Copied!" : "Copy address"}</p>
            </TooltipContent>
          </Tooltip>
        )}

        {showExplorerLink && explorerUrl && (
          <Tooltip>
            <TooltipTrigger asChild>
              <a
                className="text-muted-foreground transition-colors hover:text-foreground"
                href={explorerUrl}
                rel="noopener noreferrer"
                target="_blank"
              >
                <ExternalLinkIcon className={iconSizes[size]} />
              </a>
            </TooltipTrigger>
            <TooltipContent>
              <p>View on {chain?.name ?? "Explorer"}</p>
            </TooltipContent>
          </Tooltip>
        )}
      </span>
    </TooltipProvider>
  );
};

export const AddressDisplay = memo(PureAddressDisplay);
